/**
 * RecapPartita — a giornata calcolata, sotto il campo, il dettaglio di com'e'
 * finita (Andrea 2026-09-15): per squadra gli 11 che hanno contato con voto,
 * bonus/malus e fantavoto, poi fattore capitano, D-Factor, fairplay, totale
 * e gol; in fondo il risultato. Stessa presentazione dell'esito provvisorio,
 * ma coi conti del calcolo vero (voti finali) e visibile a tutti.
 *
 * I conti il backend li rifa' in sola lettura e ci affianca quanto salvato
 * dal calcolo della giornata: se non coincidono (voti o regole cambiati dopo
 * il calcolo) lo si dice, perche' il risultato ufficiale resta quello salvato.
 */

import { type PartitaItem, type RecapSquadra, useRecapPartita } from "../../hooks/useFase5";
import { BloccoSquadra, RisultatoFinale } from "./BloccoSquadra";
import { NOME_BONUS_DIFENSIVO } from "../common/nomenclatura";
import { tr } from "../../i18n";

interface RecapPartitaProps {
  partita: PartitaItem;
  nomeCasa: string;
  nomeTrasferta: string;
}

/** Il dettaglio rifatto adesso non coincide con quanto salvato dal calcolo. */
function nonCoincide(s: RecapSquadra): boolean {
  const salvato = s.ufficiale;
  if (!salvato) return false;
  return Math.abs(salvato.punteggio_totale - s.punteggio_totale) >= 0.005 || salvato.gol !== s.gol;
}

export function RecapPartita({ partita, nomeCasa, nomeTrasferta }: RecapPartitaProps) {
  const { data, isLoading, error } = useRecapPartita(partita.id, true);
  const errore = error as { response?: { data?: { detail?: string } }; message?: string } | null;

  const differenze = data
    ? [
        { nome: nomeCasa, s: data.casa },
        { nome: nomeTrasferta, s: data.trasferta },
      ].filter(({ s }) => nonCoincide(s))
    : [];

  return (
    <section className="card space-y-3" aria-labelledby="recap-partita-titolo">
      <div>
        <h3 id="recap-partita-titolo" className="font-display text-lg text-accent">
          {tr("Recap della partita")}
        </h3>
        <p className="text-[11px] text-fg-muted">
          Com'è andato il calcolo: voti finali, sostituzioni, fattore capitano e{" "}
          {NOME_BONUS_DIFENSIVO.toLowerCase()}.
        </p>
      </div>

      {isLoading && (
        <div className="py-6 text-center text-sm text-fg-muted">
          {tr("Caricamento del dettaglio…")}
        </div>
      )}
      {errore && !isLoading && (
        <div className="py-4 text-center text-sm text-danger">
          {errore.response?.data?.detail ?? errore.message ?? tr("Dettaglio non disponibile")}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <BloccoSquadra titolo="🏠 Casa" nome={nomeCasa} s={data.casa} />
            <BloccoSquadra titolo="✈️ Trasferta" nome={nomeTrasferta} s={data.trasferta} />
          </div>

          <RisultatoFinale
            etichetta="Risultato finale"
            nomeCasa={nomeCasa}
            nomeTrasferta={nomeTrasferta}
            golCasa={partita.gol_casa}
            golTrasferta={partita.gol_trasferta}
            fpCasa={partita.punteggio_casa}
            fpTrasferta={partita.punteggio_trasferta}
            squadraCasaId={data.casa.squadra_id}
            squadraTrasfertaId={data.trasferta.squadra_id}
          />

          {partita.rettifica && (
            <p className="text-[11px] text-warning text-center">
              {tr(
                tr(
                  "✎ Il risultato è stato modificato a mano (vedi l'avviso in alto): i conti qui sopra sono quelli del calcolo.",
                ),
              )}
            </p>
          )}
          {differenze.length > 0 && (
            <p className="text-[11px] text-warning">
              ⚠ Rifatti adesso, i conti non coincidono con quelli salvati al calcolo della giornata
              (
              {differenze
                .map(({ nome, s }) =>
                  tr("{{v1}}: salvato {{v2}} e {{v3}} gol, adesso {{v4}} e {{v5}} gol", {
                    v1: nome,
                    v2: s.ufficiale?.punteggio_totale.toFixed(1),
                    v3: s.ufficiale?.gol,
                    v4: s.punteggio_totale.toFixed(1),
                    v5: s.gol,
                  }),
                )
                .join(" · ")}
              ): voti o regole sono cambiati dopo il calcolo. Il risultato ufficiale resta quello
              salvato finché un admin non ricalcola la giornata.
            </p>
          )}
        </>
      )}
    </section>
  );
}
